<template xmlns:v-bind="http://www.w3.org/1999/xhtml" xmlns:v-el="http://www.w3.org/1999/xhtml">
  <div>
    <!-- ///////////////////////////////////////////////////////////////////////////////////////////////////// -->
    <!-- MEDIA OWNERS -->
    <form-section
      v-if="!isDealRoute && mediaOwners.length > 0"
      :icon="iconColoredMediaOwner"
      :title="capitalize($t('names.mediaOwner', { n: 2 }))"
      :can-be-removed="canBeRemoved"
      :is-read-only="isReadOnly"
      data-entity-type="media-owner"
      @cleared="clear">
      <div class="col-sm-9 m-auto">
        <selectable-expandable-small-tiles
          :items="availableMediaOwners"
          :allow-empty="false"
          :should-display-labels="true"
          :should-display-images="true"
          label-property-name="name"
          :max-items-to-show="12"
          image-source-property-name="logoUrl"
          :selected-items="selectedMediaOwners"
          :is-read-only="tilesAreReadonly"
          :is-multiselect="isMultiselect"
          :search-enabled="true"
          :search-hint="capitalize($t('actions.searchMediaOwners'))"
          :modal-title="capitalize($t('actions.browseMediaOwners'))"
          @selection-changed="onMediaOwnersSelectionChanged" />
      </div>
    </form-section>
  </div>
</template>

<script setup lang="ts">
  import { capitalize, computed, ComputedRef, markRaw, onMounted, toRefs } from 'vue';
  import { useRoute } from 'vue-router';

  import { FormSection, SelectableExpandableSmallTiles } from '@/common/components/controls';
  import IconColoredMediaOwner from '@/common/icons/colored/MediaOwner.vue';
  import { Organization, Plan } from '@/common/models';
  import { useMediaOwners } from '@/org-app/services/tanstack/queries';
  import { waitForPendingQueries } from '@/org-app/services/tanstack/queryUtils';

  interface IMediaOwnersProps {
    canBeRemoved: boolean;
    isReadOnly: boolean;
    tilesAreReadonly: boolean;
    isMultiselect: boolean;
    selectedMediaOwnersIDs: number[];
    isNew: boolean;
    entity?: Plan;
  }

  interface IMediaOwnerEmits {
    (e: 'changed', value: number[], selectedMOs: Organization[]): void;
    (e: 'cleared', value: number[]): void;
  }

  // Props
  const props = defineProps<IMediaOwnersProps>();
  const { isNew, selectedMediaOwnersIDs } = toRefs(props);

  // Emits
  const emit = defineEmits<IMediaOwnerEmits>();

  // Data
  const mediaOwnersQuery = useMediaOwners();
  const iconColoredMediaOwner = markRaw(IconColoredMediaOwner);

  // Computed
  const mediaOwners = computed(() => mediaOwnersQuery.items.value);
  const isDealRoute: ComputedRef<boolean> = computed(() => {
    const route = useRoute();
    return route.fullPath.indexOf('deals') > -1;
  });

  const activeMediaOwners: ComputedRef<Organization[]> = computed(() => mediaOwners.value.filter((x) => x.isActive));

  const availableMediaOwners = computed(() => {
    if (isNew.value) {
      return activeMediaOwners.value;
    }
    return mediaOwners.value.filter(
      (mediaOwner) => selectedMediaOwnersIDs.value?.includes(mediaOwner.id) || mediaOwner.isActive,
    );
  });

  const selectedMediaOwners: ComputedRef<Organization[]> = computed(() =>
    availableMediaOwners.value.filter((mediaOwner) => selectedMediaOwnersIDs.value?.includes(mediaOwner.id)),
  );

  // Methods
  function onMediaOwnersSelectionChanged(selectedMOs: Organization[]) {
    const selectedIDs = selectedMOs.map((x) => x.id);
    emit('changed', selectedIDs, selectedMOs);
  }

  function clear() {
    const availableIDs = availableMediaOwners.value.map((x) => x.id);
    emit('cleared', availableIDs);
  }

  // Lifecycle Hooks
  onMounted(async () => {
    await waitForPendingQueries([mediaOwnersQuery]);
    if (props.entity) {
      onMediaOwnersSelectionChanged(selectedMediaOwners.value);
    }
  });
</script>

<style lang="scss" rel="stylesheet/scss" scoped>
  .coming-soon {
    font-size: 1.2em;
    padding: 1.6rem 2.4rem;
    border-radius: 3.2rem;
    background: #fff;
    color: #0f78ff;
    border: solid 0.1rem #0f78ff44;
  }
</style>
